import React, { useEffect, useRef, useState } from "react";
import { HeaderPage } from "../base/headerPage";
import { GetSystemConfigData, getUserPrefConfigInfo, updateUserPrefConfigInfo } from "../../lib/common-service";
import { getUserCredential } from "../../lib/cookies";
import PlanCompFilter from "./filter";
import { LoadingPanel } from "src/components/loadingPanel";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { compPlanStatementSelector } from "src/redux/selectors/custom-selector";
import useCompPlanStatementService from "./service";
import CompPlanSummaryHeader from "./CompPlanSummaryHeader";
import KPIPaymentSummary from "./KPIPaymentSummary";
import CompPlanSectionTwo from "./CompPlanSectionTwo";
import ExcelExportCompPlanSectionOne from "./ExcelExportCompPlanSectionOne";
import { drawDOM, exportPDF } from "@progress/kendo-drawing";
import { renderToString } from "react-dom/server";
import PDFPageTemplate from "./PDFPageTemplate";
import { saveAs } from "@progress/kendo-file-saver";
import { getReportScreenHeight } from "src/utils/utils";
import {
  setShowReportResult,
  setShowSection1,
  setShowSection2,
  setAdvanceFilterSelectionsAction,
  setAdvanceSavedFilterAction,
  setAdvanceFilterTooltipAction,
  setAdvanceFilterDropdownsAction,
} from "src/redux/actions/compplan-actions";
import { AdvanceQueryFilter } from "../advanceQueryFilter";
import { DialogComponent } from "src/components/dialog";

const PlanCompStatementReport = (pageProps) => {
  const [isReportRunning, setIsRunningReport] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isDirectApplyLoading, setIsDirectApplyLoading] = useState(false);
  const [showAdvanceSearch, setShowAdvanceSearch] = useState(false);
  const [shouldRunReport, setShouldRunReport] = useState(false);
  const [advFilterResetSignal, setAdvFilterResetSignal] = useState(0);

  const exportSec1Ref = useRef(null);
  const exportSec2Ref = useRef(null);
  const section1ExportRef = useRef(null);
  const section2Ref = useRef(null);

  const {
    selectedCompPlan,
    selectedCompPlanPeriod,
    compPlanSection2Data,
    showSection1,
    showSection2,
    compPlanSummaryHeader,
    kpiPaymentSummary,
    showReportResults,
    isProviderDetail,
    advanceFilterSelections,
    advanceSavedFilter,
    advanceFilterTooltip,
    advFilterDropdowns,
  } = useSelector(compPlanStatementSelector, shallowEqual);

  const isAdvanceFilterApplied = advanceFilterSelections !== null;

  const [reportHeight, setReportHeight] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const reportService = useCompPlanStatementService();
  const dispatch = useDispatch();
  const cookiesData = getUserCredential();
  const locationPath = pageProps?.location?.pathname?.slice(1) || "";
  const [systemReportPeriodInfo, setSystemReportPeriodInfo] = useState({});
  const [userPrefConfigInfo, setUserPrefConfigInfo] = useState({});
  const [userPrefColumnsList, setUserPrefColumnsList] = useState("");
  const [isPeriodDataLoaded, setIsPeriodDataLoaded] = useState(false);
  const [showDiscardAdvFilterConfirm, setShowDiscardAdvFilterConfirm] = useState(false);
  const didApplyRef = useRef(false);
  const dropdownSnapshotRef = useRef(null);
  const [planCompFilterKey] = useState(0);

  useEffect(() => {
    if (isReady) {
      if (showReportResults) {
        setReportHeight(getReportScreenHeight() - 20);
      } else {
        setReportHeight(0);
      }
    }
  }, [showReportResults, isReady]);

  useEffect(() => {
    getSystemConfigInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSystemConfigInfo = async () => {
    let dataObject = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_filter: "AND system_pref_code = 'sys_report_periods'",
      in_sort_number: null,
      in_sort_order: null,
      in_page_row_offset: null,
      in_page_rows: null,
    };
    try {
      const resDataObject = await GetSystemConfigData(dataObject, null, dispatch);
      if (resDataObject && resDataObject?.length > 0) {
        setSystemReportPeriodInfo(resDataObject[0]);

        await Promise.all([
          getUserPrefConfig(resDataObject[0]?.system_pref_code),
          getUserPrefConfigForColumns("report_period_col_pref"),
        ]);
      }
    } catch (error) {
      console.error("Error fetching user preference configurations:", error);
    } finally {
      setIsPeriodDataLoaded(true);
    }
  };

  const getUserPrefConfig = async (in_user_pref_code) => {
    let dataObject = {
      in_user_pref_code,
      db_obj_code: locationPath || "",
    };
    const resDataObject = await getUserPrefConfigInfo(dataObject, null, dispatch);
    if (resDataObject && resDataObject?.length > 0) {
      setUserPrefConfigInfo(resDataObject[0]);
    }
  };

  const getUserPrefConfigForColumns = async (in_user_pref_code) => {
    let dataObject = {
      in_user_pref_code,
      db_obj_code: locationPath || "",
    };
    const resDataObject = await getUserPrefConfigInfo(dataObject, null, dispatch);
    if (resDataObject && resDataObject?.length > 0) {
      if (resDataObject[0]?.user_pref_value) {
        setUserPrefColumnsList(resDataObject[0]?.user_pref_value);
      }
    }
  };

  const handleUpdateUserPrefConfigInfo = React.useCallback(
    async (values) => {
      if (userPrefConfigInfo && systemReportPeriodInfo && !systemReportPeriodInfo?.system_pref_code) return;

      let dataObject = {
        in_user_pref_code: systemReportPeriodInfo?.system_pref_code,
        db_obj_code: locationPath || "",
        user_pref_value: values,
      };
      const resDataObject = await updateUserPrefConfigInfo(dataObject, null, dispatch);
      if (resDataObject) {
        setUserPrefConfigInfo({ ...userPrefConfigInfo, user_pref_value: values });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [systemReportPeriodInfo, locationPath, dispatch]
  );

  const handleUpdateUserPrefColumnsList = React.useCallback(
    async (values) => {
      let dataObject = {
        in_user_pref_code: "report_period_col_pref",
        db_obj_code: locationPath || "",
        user_pref_value: values,
      };
      const resDataObject = await updateUserPrefConfigInfo(dataObject, null, dispatch);
      if (resDataObject) {
        setUserPrefColumnsList(values);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [locationPath, dispatch]
  );

  const runReports = async () => {
    setIsRunningReport(true);
    dispatch(setShowReportResult(false));
    dispatch(setShowSection2(true));
    try {
      await reportService.getAutoHideSection1();
      await reportService.fetchSummaryHeaderProvider();
      await reportService.fetchPaymentSummary({
        in_filter: null,
        in_sort_number: 2,
        in_sort_order: "ASC",
      });
      await reportService.fetchReportSummaryColumnHeaders();
      await reportService.fetchReportPlanGridDetails();
    } catch (error) {
    } finally {
      setIsRunningReport(false);
    }
  };

  const handleExport = async () => {
    const option = {
      paperSize: "A4",
      landscape: true,
      repeatHeaders: true,
      allPages: true,
      forcePageBreak: ".page-break",
      avoidLinks: true,
      margin: { top: "2cm", left: "0.5cm", right: "0.5cm", bottom: "1.5cm" },
      scale: 0.5,
      template: (props) => {
        let newProps = { ...props };
        newProps.compPlan = effectiveCompPlanName;
        newProps.period = effectivePeriodName;
        let z = document.createElement("div");
        z.innerHTML = renderToString(PDFPageTemplate(newProps));
        return z;
      },
    };
    const effectiveCompPlanName = isAdvanceFilterApplied
      ? (advFilterDropdowns?.compPlan?.find(
          (p) => p.comp_plan_mstr_key === advanceFilterSelections?.selectedCompPlan?.comp_plan_mstr_key
        )?.comp_plan_name ?? selectedCompPlan?.comp_plan_name)
      : selectedCompPlan?.comp_plan_name;
    const effectivePeriodName = isAdvanceFilterApplied
      ? (advFilterDropdowns?.period?.find(
          (p) => p.period_mstr_key === advanceFilterSelections?.selectedCompPlanPeriod?.period_mstr_key
        )?.period_name ?? selectedCompPlanPeriod?.period_name)
      : selectedCompPlanPeriod?.period_name;
    try {
      setIsExporting(true);
      setTimeout(async () => {
        let group1;
        let group2;
        if (showSection1) {
          group1 = await drawDOM(exportSec1Ref.current, option);
        }
        if (showSection2) {
          await new Promise((r) => setTimeout(r, 1000));
          group2 = await drawDOM(exportSec2Ref.current, {
            ...option,
            scale: 0.46,
          });
        }

        if (group1 && group2) {
          group2.children.forEach((it) => {
            group1.children.push(it);
          });
          exportPDF(group1, { multiPage: true })
            .then((uri) => {
              saveAs(uri, "plan_comp_statement.pdf");
              setIsExporting(false);
            })
            .catch(() => {
              setIsExporting(false);
            });
        } else if (group1) {
          exportPDF(group1, { multiPage: true })
            .then((uri) => {
              saveAs(uri, "plan_comp_statement.pdf");
              setIsExporting(false);
            })
            .catch(() => {
              setIsExporting(false);
            });
        } else if (group2) {
          exportPDF(group2, { multiPage: true })
            .then((uri) => {
              saveAs(uri, "plan_comp_statement.pdf");
              setIsExporting(false);
            })
            .catch(() => {
              setIsExporting(false);
            });
        }
      }, 2000);
    } catch (error) {}
  };

  const handleExcelExport = () => {
    const sheets = [];
    const fileName = "Plan_Comp_Statement.xlsx";

    if (showSection1) {
      const section1Workbook = section1ExportRef.current?.getWorkbookOptions();
      if (section1Workbook?.sheets?.length) {
        sheets.push(...section1Workbook.sheets);
      }
    }

    if (showSection2) {
      const section2Workbook = section2Ref.current?.getWorkbookOptions();
      if (section2Workbook?.sheets?.length) {
        sheets.push(...section2Workbook.sheets);
      }
    }

    if (!sheets.length) {
      return;
    }

    const saver = section2Ref.current || section1ExportRef.current;
    saver?.saveWorkbook({ sheets }, fileName);
  };

  const canExportSection1 =
    showSection1 && (compPlanSummaryHeader.data?.length > 0 || kpiPaymentSummary.data?.length > 0);
  const canExportSection2 = showSection2 && compPlanSection2Data?.data?.length > 0;

  useEffect(() => {
    let timer = setTimeout(() => setIsReady(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (shouldRunReport) {
      setShouldRunReport(false);
      runReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRunReport]);

  const actionMenu = {
    reportSection1: {
      checked: showSection1,
      label: "Section 1",
      onChange: (checked) => {
        dispatch(setShowSection1(checked));
      },
    },
    reportSection2: {
      checked: showSection2,
      label: "Section 2",
      onChange: (checked) => {
        dispatch(setShowSection2(checked));
      },
    },
    exportToPDF: {
      caption: "Export to PDF",
      disabled: !showSection1 && !showSection2,
      fx: handleExport,
    },
    export: {
      caption: "Export to Excel",
      disabled: !canExportSection1 && !canExportSection2,
      fx: handleExcelExport,
    },
  };

  const headerConfig = {
    title: "Plan Comp Statement",
    id: "plan_comp_statement_report",
    action_menu: showReportResults ? actionMenu : undefined,
    appliedFilterQuery: advanceFilterTooltip,
    isQueryFilterApplied: isAdvanceFilterApplied,
    search_fx: (e) => {
      e?.preventDefault?.();
      didApplyRef.current = false;
      if (isAdvanceFilterApplied) {
        dropdownSnapshotRef.current = advFilterDropdowns;
      }
      setShowAdvanceSearch(true);
    },
  };
  const applyAdvanceFilter = (filterObj, providerDetail) => {
    setIsRunningReport(true);

    const normalizeSelectAll = (id) => (id === "Select All" ? "0" : id);
    const getIds = (field) =>
      (filterObj?.filters || [])
        .filter((f) => f?.field === field)
        .flatMap((f) => (Array.isArray(f.value) ? f.value : [f.value]))
        .filter(Boolean)
        .map((v) => normalizeSelectAll(String(v).replaceAll('"', "").trim()));

    const compPlanIds = getIds("comp_plan_mstr_key");
    const periodIds = getIds("period_mstr_key");
    const specialtyIds = getIds("specialty_key");
    const groupIds = getIds("org_title_key");
    const providerIds = getIds("provider_key");

    dispatch(
      setAdvanceFilterSelectionsAction({
        selectedCompPlan: compPlanIds.length ? { comp_plan_mstr_key: Number(compPlanIds[0]) } : null,
        selectedCompPlanPeriod: periodIds.length ? { period_mstr_key: Number(periodIds[0]) } : null,
        selectedSpecialities: specialtyIds.map((id) => ({ specialty_key: id === "0" ? 0 : Number(id) })),
        selectedGroups: groupIds.map((id) => ({ org_title_key: id === "0" ? 0 : Number(id) })),
        selectedProviders: providerIds.map((id) => ({ provider_key: id === "0" ? 0 : Number(id) })),
        isProviderDetail: Boolean(providerDetail),
      })
    );

    setShouldRunReport(true);
  };

  if (!isReady) {
    return <LoadingPanel />;
  }

  return (
    <div className="Main">
      <div className="content">
        <div className="content-head">
          <HeaderPage pageProps={pageProps} headerConfig={headerConfig} isQueryFilterApplied={isAdvanceFilterApplied} />
        </div>
        <div className="content-body">
          <ExcelExportCompPlanSectionOne ref={section1ExportRef} />
          <AdvanceQueryFilter
            open={showAdvanceSearch}
            resetSignal={advFilterResetSignal}
            onClose={() => {
              if (!didApplyRef.current && dropdownSnapshotRef.current !== null) {
                dispatch(setAdvanceFilterDropdownsAction(dropdownSnapshotRef.current));
              }
              dropdownSnapshotRef.current = null;
              didApplyRef.current = false;
              setShowAdvanceSearch(false);
            }}
            config={{
              dbObjName: locationPath,
              module: "plan_comp_statement_report",
              filterTableName: "vw_comp_plan_statement",
              showDefaultExpression: true,
              defaultExpressionField: "period_mstr_key",
              requiredFields: [
                { field: "comp_plan_mstr_key", label: "Comp Plan" },
                { field: "period_mstr_key", label: "Comp Plan Period" },
              ],
            }}
            initialToggleValues={{ is_provider_detail: isProviderDetail }}
            savedAppliedFilter={advanceSavedFilter}
            onDirectApplyLoading={(loading) => setIsDirectApplyLoading(loading)}
            onApply={({
              filterObj,
              filterData,
              mainLogic,
              formFields,
              gridHistoryId,
              appliedFilterTooltip: tooltip,
              appliedFilterQuery,
              toggleValues,
              filterColumns: appliedFilterColumns,
            }) => {
              didApplyRef.current = true;
              dropdownSnapshotRef.current = null;
              dispatch(
                setAdvanceSavedFilterAction({
                  filterData,
                  mainLogic,
                  formFields,
                  gridHistoryId: gridHistoryId || "",
                  appliedFilterQuery: appliedFilterQuery || "",
                  toggleValues: toggleValues || {},
                })
              );
              dispatch(setAdvanceFilterTooltipAction(tooltip || ""));
              applyAdvanceFilter(filterObj, toggleValues?.is_provider_detail);
            }}
            onSavedFilterUpdate={({
              filterData,
              mainLogic,
              formFields,
              gridHistoryId,
              appliedFilterQuery,
              toggleValues,
            }) => {
              dispatch(
                setAdvanceSavedFilterAction({
                  filterData,
                  mainLogic,
                  formFields,
                  gridHistoryId: gridHistoryId || "",
                  appliedFilterQuery: appliedFilterQuery || "",
                  toggleValues: toggleValues || {},
                })
              );
            }}
          />

          <div id="topSection">
            <PlanCompFilter
              key={planCompFilterKey}
              runReports={() => {
                if (isAdvanceFilterApplied) {
                  setShowDiscardAdvFilterConfirm(true);
                } else {
                  runReports();
                }
              }}
            />
          </div>
          <div
            style={{
              overflowY: showSection1 ? "scroll" : "hidden",
              overflowX: "hidden",
              maxHeight: reportHeight,
              marginTop: 20,
            }}
          >
            <div>
              <div ref={exportSec1Ref}>
                {showReportResults &&
                showSection1 &&
                (compPlanSummaryHeader.columns.length || compPlanSummaryHeader.data.length) ? (
                  <CompPlanSummaryHeader isExporting={isExporting} />
                ) : null}
                {showReportResults && showSection1 && kpiPaymentSummary.columns.length ? (
                  <div className="page-break">
                    <KPIPaymentSummary isReportRunning={isReportRunning} isExporting={isExporting} />
                  </div>
                ) : null}
              </div>
              {showReportResults && showSection2 ? (
                <div ref={exportSec2Ref} className={showSection1 ? "page-break" : ""}>
                  <CompPlanSectionTwo
                    ref={section2Ref}
                    isReportRunning={isReportRunning}
                    isExporting={isExporting}
                    reportHeight={reportHeight}
                    systemReportPeriodInfo={systemReportPeriodInfo}
                    userPrefConfigInfo={userPrefConfigInfo}
                    updateUserPrefConfigInfo={handleUpdateUserPrefConfigInfo}
                    userPrefColumnsList={userPrefColumnsList}
                    onUpdateUserPrefColumnsList={handleUpdateUserPrefColumnsList}
                    isPeriodDataLoaded={isPeriodDataLoaded}
                  />
                </div>
              ) : null}
            </div>
          </div>
          {isReportRunning || isExporting || isDirectApplyLoading ? <LoadingPanel /> : null}
          {showDiscardAdvFilterConfirm && (
            <DialogComponent
              title="Discard Advance Filter?"
              desc="An advance filter is currently applied. Do you want to discard it and run the report using the outer filter values?"
              visible={showDiscardAdvFilterConfirm}
              onCancelConfirm={() => setShowDiscardAdvFilterConfirm(false)}
              actionBtn={[
                {
                  name: "No",
                  onClick: () => setShowDiscardAdvFilterConfirm(false),
                },
                {
                  name: "Yes",
                  onClick: () => {
                    setShowDiscardAdvFilterConfirm(false);
                    dispatch(setAdvanceFilterSelectionsAction(null));
                    dispatch(setAdvanceSavedFilterAction(null));
                    dispatch(setAdvanceFilterTooltipAction(""));
                    dispatch(
                      setAdvanceFilterDropdownsAction({
                        compPlan: [],
                        period: [],
                        speciality: [],
                        group: [],
                        provider: [],
                      })
                    );
                    setAdvFilterResetSignal((s) => s + 1);
                    setShouldRunReport(true);
                  },
                },
              ]}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanCompStatementReport;
